import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getUtilizationData } from '../../services/api';

/* ── Column definitions ─────────────────────────────────── */

const COLUMNS = [
  // Identity
  { key: 'name', label: 'Person', sortable: true, type: 'string', group: 'identity' },
  { key: 'division', label: 'Division', sortable: true, type: 'string', group: 'identity' },
  { key: 'portfolioLead', label: 'Lead', sortable: true, type: 'string', group: 'identity' },
  { key: 'contractType', label: 'Type', sortable: true, type: 'string', group: 'identity' },
  { key: 'status', label: 'Status', sortable: true, type: 'string', group: 'identity' },
  // Availability
  { key: 'totalHoursAvailable', label: 'Hrs Avail', sortable: true, type: 'number', group: 'availability' },
  { key: 'totalHoursWorked', label: 'Hrs Worked', sortable: true, type: 'number', group: 'availability' },
  // Utilization
  { key: 'totalUtilization', label: 'Total Ute %', sortable: true, type: 'number', group: 'utilization' },
  { key: 'billableHours', label: 'Bill Hrs', sortable: true, type: 'number', group: 'utilization' },
  { key: 'billableUtilization', label: 'Bill Ute %', sortable: true, type: 'number', group: 'utilization' },
  { key: 'directUtilization', label: 'Direct Ute %', sortable: true, type: 'number', group: 'utilization' },
  // Indirect
  { key: 'overheadHours', label: 'OH', sortable: true, type: 'number', group: 'indirect' },
  { key: 'bpHours', label: 'B&P', sortable: true, type: 'number', group: 'indirect' },
  { key: 'gaHours', label: 'G&A', sortable: true, type: 'number', group: 'indirect' },
  { key: 'irdHours', label: 'IR&D', sortable: true, type: 'number', group: 'indirect' },
  { key: 'totalIndirect', label: 'Total', sortable: true, type: 'number', group: 'indirect' },
  { key: 'indirectNotOffset', label: 'Not Offset', sortable: true, type: 'number', group: 'indirect' },
  { key: 'pctIndirectHit', label: '% Hit', sortable: true, type: 'number', group: 'indirect' },
  // Time Off
  { key: 'holidayHours', label: 'Holiday', sortable: true, type: 'number', group: 'timeoff' },
  { key: 'ptoHours', label: 'PTO', sortable: true, type: 'number', group: 'timeoff' },
  { key: 'totalTimeOff', label: 'Total', sortable: true, type: 'number', group: 'timeoff' },
  { key: 'pctTimeOffHit', label: '% Hit', sortable: true, type: 'number', group: 'timeoff' },
  // Other
  { key: 'ptoBalance', label: 'PTO Bal', sortable: true, type: 'number', group: 'other' },
];

const COL_GROUPS = [
  { label: 'Identity', span: 5 },
  { label: 'Availability', span: 2 },
  { label: 'Utilization', span: 4 },
  { label: 'Indirect', span: 7 },
  { label: 'Time Off', span: 4 },
  { label: 'Other', span: 1 },
];

/* ── Helpers ─────────────────────────────────────────────── */

function compareValues(a, b, key, type) {
  let valA = a[key];
  let valB = b[key];
  if (type === 'number') {
    valA = Number(valA) || 0;
    valB = Number(valB) || 0;
    return valA - valB;
  }
  return (valA || '').toLowerCase().localeCompare((valB || '').toLowerCase());
}

function uteClass(value) {
  if (value >= 85) return 'ute-green';
  if (value >= 70) return 'ute-yellow';
  return 'ute-red';
}

function fmtPct(value) {
  return value != null ? value.toFixed(1) + '%' : '\u2014';
}

function fmtNum(value) {
  return value != null ? value.toLocaleString() : '\u2014';
}

const PAGE_SIZES = [25, 50, 100, 0]; // 0 = show all

// Division tabs — labels that match Ryan's spreadsheet tab names
const DIVISION_TABS = [
  { key: 'All', label: 'All' },
  { key: 'MS1', label: 'MS1' },
  { key: 'MS2', label: 'MS2' },
  { key: 'IS', label: 'IS' },
  { key: 'BL', label: 'BL' },
  { key: 'Corp', label: 'Corp' },
];

// Which columns should be summed vs averaged in the totals row
const SUM_KEYS = new Set([
  'totalHoursAvailable', 'totalHoursWorked',
  'billableHours', 'nonBillableProjectHours', 'totalProjectHours',
  'overheadHours', 'bpHours', 'gaHours', 'irdHours', 'totalIndirect', 'indirectNotOffset',
  'holidayHours', 'ptoHours', 'lwopHours', 'otherTimeOff', 'totalTimeOff',
  'ptoBalance',
]);
const AVG_KEYS = new Set([
  'totalUtilization', 'billableUtilization', 'directUtilization',
  'pctIndirectHit', 'pctTimeOffHit',
]);

const CALENDAR_YEAR = '2026';

// Delivery Cognito group → division key mapping
const DELIVERY_GROUP_MAP = {
  'div-ms1': 'MS1',
  'div-ms2': 'MS2',
  'div-is': 'IS',
  'div-bl': 'BL',
  'div-corp': 'Corp',
};
const FULL_ACCESS_GROUPS = ['admin', 'senior-leader', 'investor'];

/* ── Component ───────────────────────────────────────────── */

function DeliveryDashboard({ onNavigate, user }) {
  // RBAC: determine user's delivery access scope
  const groups = useMemo(() => user?.groups || [], [user]);
  const canSeeAll = useMemo(() => FULL_ACCESS_GROUPS.some(g => groups.includes(g)), [groups]);

  // Find user's assigned division from their Cognito group membership
  const userDivision = useMemo(() => {
    if (canSeeAll) return null;
    for (const [group, div] of Object.entries(DELIVERY_GROUP_MAP)) {
      if (groups.includes(group)) return div;
    }
    return null; // no delivery div group → fallback to all (migration)
  }, [groups, canSeeAll]);

  // Determine which division tabs to show
  const visibleTabs = useMemo(() => {
    if (canSeeAll) return DIVISION_TABS;
    if (userDivision) return DIVISION_TABS.filter(t => t.key === userDivision);
    return DIVISION_TABS; // fallback: user group without div-* → show all
  }, [canSeeAll, userDivision]);

  // API data state
  const [employees, setEmployees] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Period selection state
  const [periodStart, setPeriodStart] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [availablePeriods, setAvailablePeriods] = useState([]);

  // Filter state
  const [division, setDivision] = useState(() => {
    if (canSeeAll) return 'All';
    if (userDivision) return userDivision;
    return 'All'; // fallback for unscoped users
  });
  const [portfolioLead, setPortfolioLead] = useState('All');
  const [contractType, setContractType] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');

  // Sort state
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Pagination state
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch utilization data from API (re-fetches when period changes)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    async function load() {
      try {
        const apiDivision = canSeeAll ? 'All' : division;
        const result = await getUtilizationData(CALENDAR_YEAR, periodStart, periodEnd, apiDivision);
        if (!cancelled) {
          setEmployees(result.employees || []);
          // Capture metadata
          const { employees: _, ...meta } = result;
          if (Object.keys(meta).length > 0) setMetadata(meta);
          // Set available periods from API response
          if (result.available_periods) {
            setAvailablePeriods(result.available_periods);
          }
          // Set default period on first load (when no period selected yet)
          if (!periodStart && result.data_start) {
            setPeriodStart(result.data_start);
            setPeriodEnd(result.data_end);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [periodStart, periodEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive filter options from real data
  const filterOptions = useMemo(() => {
    const leads = new Set();
    const types = new Set();
    const statuses = new Set();
    employees.forEach((p) => {
      if (p.portfolioLead) leads.add(p.portfolioLead);
      if (p.contractType) types.add(p.contractType);
      if (p.status) statuses.add(p.status);
    });
    return {
      portfolioLeads: ['All', ...Array.from(leads).sort()],
      contractTypes: ['All', ...Array.from(types).sort()],
      statuses: ['All', ...Array.from(statuses).sort()],
    };
  }, [employees]);

  // Filter + sort
  const filtered = useMemo(() => {
    let list = employees.filter((p) => {
      if (division !== 'All' && p.division !== division) return false;
      if (portfolioLead !== 'All' && p.portfolioLead !== portfolioLead) return false;
      if (contractType !== 'All' && p.contractType !== contractType) return false;
      if (statusFilter !== 'All' && p.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!p.name.toLowerCase().includes(q)) return false;
      }
      return true;
    });

    const colDef = COLUMNS.find((c) => c.key === sortKey);
    const type = colDef ? colDef.type : 'string';
    list = [...list].sort((a, b) => {
      const cmp = compareValues(a, b, sortKey, type);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [employees, division, portfolioLead, contractType, statusFilter, search, sortKey, sortDir]);

  // Reset to page 1 when filters change
  const filterKey = `${division}|${portfolioLead}|${contractType}|${statusFilter}|${search}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey);
    setCurrentPage(1);
  }

  // Pagination math
  const totalPages = pageSize === 0 ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = pageSize === 0 ? filtered : filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const rangeStart = filtered.length === 0 ? 0 : (pageSize === 0 ? 1 : (currentPage - 1) * pageSize + 1);
  const rangeEnd = pageSize === 0 ? filtered.length : Math.min(currentPage * pageSize, filtered.length);

  // KPIs — weighted averages: sum(hours) / sum(available), NOT avg of individual %s
  const kpis = useMemo(() => {
    const count = filtered.length;
    if (count === 0) {
      return { headcount: 0, avgBillableUte: '0.0', totalBillableHrs: 0, avgTotalUte: '0.0' };
    }
    const totalBillableHrs = filtered.reduce((s, p) => s + (p.billableHours || 0), 0);
    const totalAvailHrs = filtered.reduce((s, p) => s + (p.totalHoursAvailable || 0), 0);
    const totalHrsWorked = filtered.reduce((s, p) => s + (p.totalHoursWorked || 0), 0);
    const avgBillableUte = totalAvailHrs > 0 ? ((totalBillableHrs / totalAvailHrs) * 100).toFixed(1) : '0.0';
    const avgTotalUte = totalAvailHrs > 0 ? ((totalHrsWorked / totalAvailHrs) * 100).toFixed(1) : '0.0';
    return { headcount: count, avgBillableUte, totalBillableHrs, avgTotalUte };
  }, [filtered]);

  // FFP vs T&M split metrics — weighted avg bill ute
  const typeSplit = useMemo(() => {
    const fp = filtered.filter((p) => p.contractType === 'FP');
    const lh = filtered.filter((p) => p.contractType === 'LH');
    const calc = (list) => {
      const n = list.length;
      if (n === 0) return { count: 0, avgBillUte: '0.0', totalBillHrs: 0 };
      const totalBillHrs = list.reduce((s, p) => s + (p.billableHours || 0), 0);
      const totalAvail = list.reduce((s, p) => s + (p.totalHoursAvailable || 0), 0);
      const avgBillUte = totalAvail > 0 ? ((totalBillHrs / totalAvail) * 100).toFixed(1) : '0.0';
      return { count: n, avgBillUte, totalBillHrs };
    };
    return { fp: calc(fp), lh: calc(lh) };
  }, [filtered]);

  // Division counts for tab badges (computed before division filter)
  const divisionCounts = useMemo(() => {
    // Apply all filters EXCEPT division to get accurate counts
    const base = employees.filter((p) => {
      if (portfolioLead !== 'All' && p.portfolioLead !== portfolioLead) return false;
      if (contractType !== 'All' && p.contractType !== contractType) return false;
      if (statusFilter !== 'All' && p.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!p.name.toLowerCase().includes(q)) return false;
      }
      return true;
    });
    const counts = { All: base.length };
    visibleTabs.forEach((t) => {
      if (t.key !== 'All') counts[t.key] = base.filter((p) => p.division === t.key).length;
    });
    return counts;
  }, [employees, portfolioLead, contractType, statusFilter, search, visibleTabs]);

  // Summary totals row — sums for hours, weighted averages for percentages
  const totals = useMemo(() => {
    const count = filtered.length;
    if (count === 0) return null;
    const row = {};
    // Sum all hour columns first
    COLUMNS.forEach((col) => {
      if (SUM_KEYS.has(col.key)) {
        row[col.key] = filtered.reduce((s, p) => s + (Number(p[col.key]) || 0), 0);
      }
    });
    // Weighted percentages: each person's % weighted by their available hours
    const totalAvail = row.totalHoursAvailable || 0;
    AVG_KEYS.forEach((key) => {
      if (totalAvail > 0) {
        row[key] = filtered.reduce((s, p) =>
          s + (Number(p[key]) || 0) * (Number(p.totalHoursAvailable) || 0), 0) / totalAvail;
      } else {
        row[key] = 0;
      }
    });
    return row;
  }, [filtered]);

  // CSV export
  const handleExport = useCallback(() => {
    const header = COLUMNS.map((c) => c.label).join(',');
    const rows = filtered.map((p) =>
      COLUMNS.map((col) => {
        const val = p[col.key];
        if (val == null) return '';
        if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
        return val;
      }).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `utilization-export-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  // Sort handler
  const handleSort = useCallback(
    (key) => {
      if (key !== sortKey) {
        setSortKey(key);
        setSortDir('asc');
      } else if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        setSortKey('name');
        setSortDir('asc');
      }
    },
    [sortKey, sortDir],
  );

  // Import date label from metadata
  const importedLabel = useMemo(() => {
    const ts = metadata?.uploaded_at || metadata?.imported_at || metadata?.importedAt;
    if (!ts) return null;
    try {
      const d = new Date(ts);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return null; }
  }, [metadata]);

  /* ── Render ─── */

  // Loading state
  if (loading) {
    return <div className="state-loading">Loading utilization data...</div>;
  }

  // Error state
  if (error) {
    return <div className="state-error">Unable to load utilization data: {error}</div>;
  }

  // Empty state — no data uploaded yet
  if (employees.length === 0) {
    return (
      <div className="state-empty" style={{ textAlign: 'center', padding: '80px 16px', color: 'var(--bcore-text-dim)' }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16, opacity: 0.4 }}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No utilization data available</div>
        <div>Upload a UTE tracker spreadsheet via the UTE Import page to populate this dashboard.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Period Selector Banner */}
      <div className="delivery-date-banner">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span className="delivery-date-banner-label">From</span>
        <select
          className="period-select"
          value={periodStart || ''}
          onChange={(e) => { setPeriodStart(e.target.value); setCurrentPage(1); }}
        >
          {availablePeriods.filter(p => !periodEnd || p.key <= periodEnd).map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
          {availablePeriods.length === 0 && <option value="">—</option>}
        </select>
        <span className="delivery-date-banner-label">To</span>
        <select
          className="period-select"
          value={periodEnd || ''}
          onChange={(e) => { setPeriodEnd(e.target.value); setCurrentPage(1); }}
        >
          {availablePeriods.filter(p => !periodStart || p.key >= periodStart).map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
          {availablePeriods.length === 0 && <option value="">—</option>}
        </select>
        <span className="delivery-date-banner-sep">&middot;</span>
        <span className="delivery-date-banner-dim">{employees.length} employees</span>
        {importedLabel && (
          <>
            <span className="delivery-date-banner-sep">&middot;</span>
            <span className="delivery-date-banner-dim">Imported {importedLabel}</span>
          </>
        )}
      </div>

      {/* KPI Cards */}
      <div className="stat-cards" style={{ marginBottom: 24 }}>
        {/* Headcount */}
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">
              HEADCOUNT
              <span className="kpi-hint" data-tooltip="Count of employee rows matching the current filters.">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </span>
            </span>
            <div className="stat-card-icon accent">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87" />
                <path d="M16 3.13a4 4 0 010 7.75" />
              </svg>
            </div>
          </div>
          <div className="stat-card-value">{kpis.headcount}</div>
          <div className="stat-card-sub">active employees in view</div>
        </div>

        {/* Avg Billable Ute */}
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">
              AVG BILLABLE UTE
              <span className="kpi-hint" data-tooltip="Weighted: Sum of all Billable Hours ÷ Sum of all Available Hours × 100. Not an average of individual percentages.">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </span>
            </span>
            <div className="stat-card-icon green">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </div>
          </div>
          <div className="stat-card-value">{kpis.avgBillableUte}%</div>
          <div className="stat-card-sub">target: 85%</div>
        </div>

        {/* Total Billable Hours */}
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">
              TOTAL BILLABLE HRS
              <span className="kpi-hint" data-tooltip="Sum of all Billable Hours across filtered employees.">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </span>
            </span>
            <div className="stat-card-icon blue">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
          </div>
          <div className="stat-card-value">{kpis.totalBillableHrs.toLocaleString()}</div>
          <div className="stat-card-sub">across filtered employees</div>
        </div>

        {/* Avg Total Ute */}
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">
              AVG TOTAL UTE
              <span className="kpi-hint" data-tooltip="Weighted: Sum of all Hours Worked ÷ Sum of all Available Hours × 100. Includes billable, indirect, and all other logged time.">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </span>
            </span>
            <div className="stat-card-icon accent">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            </div>
          </div>
          <div className="stat-card-value">{kpis.avgTotalUte}%</div>
          <div className="stat-card-sub">all hours logged</div>
        </div>
      </div>

      {/* FFP vs T&M Split */}
      <div className="delivery-type-split">
        <div className="delivery-type-split-item">
          <span className="delivery-type-split-label">FFP</span>
          <span className="delivery-type-split-value">{typeSplit.fp.count}</span>
          <span className="delivery-type-split-dim">employees</span>
          <span className="delivery-type-split-sep">&middot;</span>
          <span className={`delivery-ute-badge ${uteClass(Number(typeSplit.fp.avgBillUte))}`}>
            {typeSplit.fp.avgBillUte}%
          </span>
          <span className="delivery-type-split-dim">avg bill ute</span>
          <span className="delivery-type-split-sep">&middot;</span>
          <span className="delivery-type-split-value">{typeSplit.fp.totalBillHrs.toLocaleString()}</span>
          <span className="delivery-type-split-dim">bill hrs</span>
        </div>
        <div className="delivery-type-split-item">
          <span className="delivery-type-split-label">T&amp;M</span>
          <span className="delivery-type-split-value">{typeSplit.lh.count}</span>
          <span className="delivery-type-split-dim">employees</span>
          <span className="delivery-type-split-sep">&middot;</span>
          <span className={`delivery-ute-badge ${uteClass(Number(typeSplit.lh.avgBillUte))}`}>
            {typeSplit.lh.avgBillUte}%
          </span>
          <span className="delivery-type-split-dim">avg bill ute</span>
          <span className="delivery-type-split-sep">&middot;</span>
          <span className="delivery-type-split-value">{typeSplit.lh.totalBillHrs.toLocaleString()}</span>
          <span className="delivery-type-split-dim">bill hrs</span>
        </div>
      </div>

      {/* Division Tabs */}
      <div className="tab-bar" style={{ marginBottom: 12 }}>
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab-btn${division === tab.key ? ' active' : ''}`}
            onClick={() => { setDivision(tab.key); setCurrentPage(1); }}
          >
            {tab.label}
            <span className="count">{divisionCounts[tab.key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="delivery-filter-bar">
        <select className="filter-select" value={portfolioLead} onChange={(e) => setPortfolioLead(e.target.value)}>
          {filterOptions.portfolioLeads.map((pl) => (
            <option key={pl} value={pl}>{pl === 'All' ? 'All Leads' : pl}</option>
          ))}
        </select>
        <select className="filter-select" value={contractType} onChange={(e) => setContractType(e.target.value)}>
          {filterOptions.contractTypes.map((ct) => (
            <option key={ct} value={ct}>{ct === 'All' ? 'All Types' : ct}</option>
          ))}
        </select>
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {filterOptions.statuses.map((s) => (
            <option key={s} value={s}>{s === 'All' ? 'All Status' : s}</option>
          ))}
        </select>
        <div className="delivery-search-wrapper">
          <input
            type="text"
            className="form-input"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Results Count + Export */}
      <div className="delivery-results-count">
        <span>{filtered.length} employees match filters</span>
        <button
          className="btn btn-outline btn-sm"
          onClick={handleExport}
          disabled={filtered.length === 0}
          style={{ marginLeft: 'auto' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: 'middle' }}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Utilization Table */}
      <div className="data-table-wrapper">
        <table className="data-table delivery-table">
          <thead>
            {/* Column group header row */}
            <tr className="delivery-col-groups">
              {COL_GROUPS.map((g) => (
                <th key={g.label} colSpan={g.span}>{g.label}</th>
              ))}
            </tr>
            {/* Individual column headers */}
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={col.sortable ? 'sortable-th' : ''}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  style={{ cursor: col.sortable ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                >
                  {col.label}
                  {col.sortable && sortKey === col.key && (
                    <span className="sort-indicator" style={{ marginLeft: 4, fontSize: 10 }}>
                      {sortDir === 'asc' ? '\u25B2' : '\u25BC'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((person) => (
              <tr key={person.id || person.name}>
                {/* Identity */}
                <td className="cell-primary" style={{ whiteSpace: 'nowrap' }}>
                  {person.name}
                  {person.firstDay && <span className="delivery-badge-new" title={`Started ${person.firstDay}`}>NEW</span>}
                  {person.lastDay && <span className="delivery-badge-departing" title={`Last day ${person.lastDay}`}>DEPARTING</span>}
                </td>
                <td>{person.division}</td>
                <td>{person.portfolioLead || '\u2014'}</td>
                <td>{person.contractType || '\u2014'}</td>
                <td>{person.status}</td>
                {/* Availability */}
                <td className="delivery-group-start">{fmtNum(person.totalHoursAvailable)}</td>
                <td>{fmtNum(person.totalHoursWorked)}</td>
                {/* Utilization */}
                <td className="delivery-group-start">{fmtPct(person.totalUtilization)}</td>
                <td>{fmtNum(person.billableHours)}</td>
                <td>
                  <span className={`delivery-ute-badge ${uteClass(person.billableUtilization)}`}>
                    {fmtPct(person.billableUtilization)}
                  </span>
                </td>
                <td>{fmtPct(person.directUtilization)}</td>
                {/* Indirect */}
                <td className="delivery-group-start">{fmtNum(person.overheadHours)}</td>
                <td>{fmtNum(person.bpHours)}</td>
                <td>{fmtNum(person.gaHours)}</td>
                <td>{fmtNum(person.irdHours)}</td>
                <td>{fmtNum(person.totalIndirect)}</td>
                <td>{fmtNum(person.indirectNotOffset)}</td>
                <td>{fmtPct(person.pctIndirectHit)}</td>
                {/* Time Off */}
                <td className="delivery-group-start">{fmtNum(person.holidayHours)}</td>
                <td>{fmtNum(person.ptoHours)}</td>
                <td>{fmtNum(person.totalTimeOff)}</td>
                <td>{fmtPct(person.pctTimeOffHit)}</td>
                {/* Other — PTO Balance with negative alert */}
                <td className="delivery-group-start">
                  {person.ptoBalance < 0 ? (
                    <span className="delivery-pto-negative">{fmtNum(person.ptoBalance)}</span>
                  ) : (
                    fmtNum(person.ptoBalance)
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--bcore-text-dim)' }}
                >
                  No employees match the current filters
                </td>
              </tr>
            )}
          </tbody>
          {totals && (
            <tfoot>
              <tr className="delivery-totals-row">
                {/* Identity — label + blanks */}
                <td className="cell-primary" style={{ whiteSpace: 'nowrap' }}>
                  TOTALS ({filtered.length})
                </td>
                <td>&mdash;</td>
                <td>&mdash;</td>
                <td>&mdash;</td>
                <td>&mdash;</td>
                {/* Availability */}
                <td className="delivery-group-start">{fmtNum(totals.totalHoursAvailable)}</td>
                <td>{fmtNum(totals.totalHoursWorked)}</td>
                {/* Utilization */}
                <td className="delivery-group-start">{fmtPct(totals.totalUtilization)}</td>
                <td>{fmtNum(totals.billableHours)}</td>
                <td>
                  <span className={`delivery-ute-badge ${uteClass(totals.billableUtilization)}`}>
                    {fmtPct(totals.billableUtilization)}
                  </span>
                </td>
                <td>{fmtPct(totals.directUtilization)}</td>
                {/* Indirect */}
                <td className="delivery-group-start">{fmtNum(totals.overheadHours)}</td>
                <td>{fmtNum(totals.bpHours)}</td>
                <td>{fmtNum(totals.gaHours)}</td>
                <td>{fmtNum(totals.irdHours)}</td>
                <td>{fmtNum(totals.totalIndirect)}</td>
                <td>{fmtNum(totals.indirectNotOffset)}</td>
                <td>{fmtPct(totals.pctIndirectHit)}</td>
                {/* Time Off */}
                <td className="delivery-group-start">{fmtNum(totals.holidayHours)}</td>
                <td>{fmtNum(totals.ptoHours)}</td>
                <td>{fmtNum(totals.totalTimeOff)}</td>
                <td>{fmtPct(totals.pctTimeOffHit)}</td>
                {/* Other — PTO Balance sum */}
                <td className="delivery-group-start">
                  {totals.ptoBalance < 0 ? (
                    <span className="delivery-pto-negative">{fmtNum(totals.ptoBalance)}</span>
                  ) : (
                    fmtNum(totals.ptoBalance)
                  )}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {filtered.length > 0 && (
        <div className="pagination-bar">
          <div className="pagination-info">
            Showing {rangeStart}&ndash;{rangeEnd} of {filtered.length}
          </div>
          <div className="pagination-controls">
            <button
              className="btn btn-outline btn-sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="pagination-page">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn btn-outline btn-sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
          <div className="pagination-size">
            <span className="pagination-size-label">Rows per page</span>
            <select
              className="filter-select"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s === 0 ? 'All' : s}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryDashboard;
